export type QueryParamEntry = { key: string; value: string };

export type QueryParamRowLike = {
  key?: string | null;
  value?: string | null;
  enabled?: boolean;
};

/** Percent-decode a query key/value without treating `+` as a space. */
export function decodeQueryParamComponent(s: string): string {
  const raw = String(s);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * Parse Path/URL query into raw logical values.
 * `100+50` stays `100+50`; `%2B` decodes to `+`. Do not convert `+` to space.
 */
export function parsePathAndQueryString(pathOrUrl: string): { path: string; params: QueryParamEntry[] } {
  const qIndex = pathOrUrl.indexOf('?');
  if (qIndex === -1) {
    return { path: pathOrUrl, params: [] };
  }
  const path = pathOrUrl.slice(0, qIndex);
  const queryString = pathOrUrl.slice(qIndex + 1);
  const params: QueryParamEntry[] = [];
  if (!queryString) {
    return { path, params };
  }
  queryString.split('&').forEach((segment) => {
    const eqIndex = segment.indexOf('=');
    let key = '';
    let value = '';
    if (eqIndex === -1) {
      key = decodeQueryParamComponent(segment.trim());
    } else {
      key = decodeQueryParamComponent(segment.slice(0, eqIndex).trim());
      value = decodeQueryParamComponent(segment.slice(eqIndex + 1));
    }
    params.push({ key, value });
  });
  return { path, params };
}

/**
 * Serialize Query Params table values.
 * Display keeps the raw logical value (`100+50`); wire encodes once (`100%2B50`).
 */
export function serializeQueryParamEntries(
  queryParamsArray: QueryParamRowLike[],
  options?: { forDisplay?: boolean }
): string {
  const forDisplay = options?.forDisplay === true;
  const parts: string[] = [];
  for (const param of queryParamsArray) {
    if (param.enabled === false || param.key == null || String(param.key).trim() === '') {
      continue;
    }
    const rawKey = String(param.key).trim();
    const rawValue = String(param.value ?? '');
    if (forDisplay || /\{\{/.test(rawKey) || /\{\{/.test(rawValue)) {
      parts.push(`${rawKey}=${rawValue}`);
      continue;
    }
    const params = new URLSearchParams();
    params.append(rawKey, rawValue);
    parts.push(params.toString());
  }
  return parts.join('&');
}

/** Path + query params. Display vs wire encoding is controlled by `options.forDisplay`. */
export function buildUrlFromPathAndQueryParams(
  pathOnly: string,
  queryParamsArray: QueryParamRowLike[],
  options?: { forDisplay?: boolean }
): string {
  const queryString = serializeQueryParamEntries(queryParamsArray, options);
  return queryString ? `${pathOnly}?${queryString}` : pathOnly;
}

/**
 * Join Environment base URL with a relative Path (query string on the path is kept as-is).
 * Does not encode. Encoding happens once via {@link serializeQueryParamEntries} / {@link replaceUrlQuery}.
 */
export function joinBaseUrlAndRelativePath(baseUrl: string, relativeUrl: string): string {
  const base = String(baseUrl ?? '').trim().replace(/\/+$/, '');
  const rel = String(relativeUrl ?? '').trim();
  if (!rel) return base;
  if (/^https?:\/\//i.test(rel)) return rel;
  const path = rel.startsWith('/') ? rel.slice(1) : rel;
  return base ? `${base}/${path}` : `/${path}`;
}

/**
 * Replace the query string using origin + pathname from the URL object and serialized entries.
 * Never uses URL.searchParams.toString() (that turns literal `+` into a space).
 */
export function replaceUrlQuery(url: string, queryEntries: QueryParamRowLike[]): string {
  const search = serializeQueryParamEntries(queryEntries);
  const isAbsolute = /^https?:\/\//i.test(url);
  try {
    const parsed = new URL(url, 'http://placeholder');
    const pathAndOrigin = isAbsolute ? `${parsed.origin}${parsed.pathname}` : parsed.pathname;
    return search ? `${pathAndOrigin}?${search}` : pathAndOrigin;
  } catch {
    const base = url.includes('?') ? url.slice(0, url.indexOf('?')) : url;
    return search ? `${base}?${search}` : base;
  }
}

/** Drop query keys (e.g. path-param names) without re-parsing `+` as space. */
export function stripQueryKeysFromUrl(url: string, keysToStrip: Iterable<string>): string {
  const lower = new Set(
    Array.from(keysToStrip)
      .map((k) => String(k ?? '').trim().toLowerCase())
      .filter(Boolean)
  );
  if (lower.size === 0) return url;
  const kept = parsePathAndQueryString(url).params.filter(
    (p) => !lower.has(String(p.key).trim().toLowerCase())
  );
  return replaceUrlQuery(url, kept);
}

/** Set/replace one query key (API Key Add to Query Params) without rewriting `+` / `*` via URL.toString(). */
export function setQueryParamOnUrl(url: string, key: string, value: string): string {
  const k = String(key ?? '').trim();
  if (!k) return url;
  const without = parsePathAndQueryString(url).params.filter(
    (p) => String(p.key).trim().toLowerCase() !== k.toLowerCase()
  );
  return replaceUrlQuery(url, [...without, { key: k, value: value != null ? String(value) : '' }]);
}

/**
 * Rebuild an absolute URL with query encoded exactly once.
 * Logical `100+2` becomes wire `100%2B2`. Already-encoded `%2B` is not double-encoded.
 * `*` stays `*` (Postman URL-bar / form-urlencoded).
 */
export function toAbsoluteWireUrl(absoluteUrl: string): string {
  const trimmed = String(absoluteUrl ?? '').trim();
  const parsed = new URL(trimmed);
  const { params } = parsePathAndQueryString(trimmed);
  const search = serializeQueryParamEntries(params);
  return `${parsed.protocol}//${parsed.host}${parsed.pathname}${search ? `?${search}` : ''}`;
}

/**
 * Path query is kept when the Params table has the same key with an empty value.
 * Table rows with a non-empty value win for that key.
 */
export function mergePathQueryAndParamsTabRows<T extends QueryParamRowLike>(
  parsedPathParams: T[],
  latestQueryParams: T[]
): { pathFallback: T[]; paramsTabDeduped: T[] } {
  const tabKeysWithNonEmptyValue = new Set(
    latestQueryParams
      .filter(
        (p) =>
          p.key != null &&
          String(p.key).trim() !== '' &&
          String(p.value ?? '').trim() !== ''
      )
      .map((p) => String(p.key).trim().toLowerCase())
  );
  const pathFallback = parsedPathParams.filter(
    (p) =>
      p.key != null &&
      String(p.key).trim() !== '' &&
      !tabKeysWithNonEmptyValue.has(String(p.key).trim().toLowerCase())
  );
  const pathKeysLower = new Set(
    parsedPathParams
      .filter((p) => p.key != null && String(p.key).trim() !== '')
      .map((p) => String(p.key).trim().toLowerCase())
  );
  const paramsTabDeduped = latestQueryParams.filter((p) => {
    if (p.key == null || String(p.key).trim() === '') return true;
    const k = String(p.key).trim().toLowerCase();
    if (!pathKeysLower.has(k)) return true;
    return String(p.value ?? '').trim() !== '';
  });
  return { pathFallback, paramsTabDeduped };
}

export function combinedQueryParamsFromPathAndTable<T extends QueryParamRowLike>(
  parsedPathParams: T[],
  latestQueryParams: T[]
): T[] {
  const { pathFallback, paramsTabDeduped } = mergePathQueryAndParamsTabRows(
    parsedPathParams,
    latestQueryParams
  );
  return [...pathFallback, ...paramsTabDeduped];
}

/** Query Params tab values stored on request.description (JSON array or legacy object). */
export function parseQueryParamEntriesFromRequestDescription(
  description: string | null | undefined
): QueryParamEntry[] {
  if (!description) {
    return [];
  }
  try {
    const parsed = JSON.parse(description);
    if (Array.isArray(parsed)) {
      return parsed
        .filter(
          (p: { key?: unknown; enabled?: boolean }) =>
            p && p.enabled !== false && p.key != null && String(p.key).trim() !== ''
        )
        .map((p: { key?: unknown; value?: unknown }) => ({
          key: String(p.key).trim(),
          value: p.value != null ? String(p.value) : '',
        }));
    }
    if (parsed && typeof parsed === 'object') {
      return Object.entries(parsed as Record<string, unknown>)
        .filter(([k]) => k != null && String(k).trim() !== '')
        .map(([k, v]) => ({
          key: String(k).trim(),
          value: v != null ? String(v) : '',
        }));
    }
  } catch {
    /* ignore invalid JSON */
  }
  return [];
}

export function recordToQueryParamEntries(record: Record<string, string>): QueryParamEntry[] {
  return Object.entries(record)
    .filter(([k]) => k != null && String(k).trim() !== '')
    .map(([k, v]) => ({
      key: String(k).trim(),
      value: v != null ? String(v) : '',
    }));
}

/** Row values first; Params tab entries override keys already present (Postman-style). Duplicate keys in tab are kept. */
export function mergeQueryParamEntriesForExecution(
  rowEntries: QueryParamEntry[],
  tabEntries: QueryParamEntry[]
): QueryParamEntry[] {
  const tabKeys = new Set(tabEntries.map((e) => e.key.toLowerCase()));
  const filteredRow = rowEntries.filter((e) => !tabKeys.has(e.key.toLowerCase()));
  return [...filteredRow, ...tabEntries];
}

/** Replace URL search with query entries (preserves duplicate keys; encodes once). */
export function applyQueryParamEntriesToUrl(
  url: string,
  queryEntries: QueryParamEntry[],
  substitute: (value: string) => string
): string {
  const entries = queryEntries.filter(({ key }) => key != null && String(key).trim() !== '');
  if (entries.length === 0) {
    return url;
  }
  const substituted = entries.map(({ key, value }) => ({
    key: substitute(String(key).trim()),
    value: substitute(value != null ? String(value) : ''),
  }));
  return replaceUrlQuery(url, substituted);
}
